import { useState, useEffect, useCallback, useRef } from 'react';
import apiService from '../services/apiService';
import { userErrorMessage } from '../utils/userError';

// A 404 on DELETE means the conversation is already gone (a repeated click),
// which is success (#433). apiService errors carry `status` since #439; the
// message regex is the fallback for anything that throws a bare Error.
const isAlreadyGone = (err) => err?.status === 404 || /\b404\b/.test(String(err?.message || ''));

export const useConversations = (token = null, authStable = false) => {
    const [conversations, setConversations] = useState([]);
    const [currentConversation, setCurrentConversation] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    // #206 (re-open): once the conversation LIST has been loaded for this session,
    // subsequent auth-driven refetches are SILENT (no `loading` flag) — see the
    // token effect below for the "ghost fetch" this defends against.
    const hasLoadedListRef = useRef(false);
    // Inconsistent deletion (#433). Conversations deleted (or being deleted) in
    // this session. A LIST response can be stale — issued before the DELETE
    // committed, answered after it — and would re-add the row the user just
    // removed ("stays in the list indefinitely"). Every list write is filtered
    // through this tombstone set, and a delete bumps `listReqRef` so an
    // in-flight list fetch is dropped outright (last-ISSUED-wins, the
    // ExpensesPanel #294/#428 pattern). Ids are UUIDs: a tombstone never needs
    // to be lifted except when its own delete FAILS (the row is restored).
    const tombstonesRef = useRef(new Set());
    const listReqRef = useRef(0);
    // The latest RENDERED list — read synchronously by removeLocally, because a
    // setState updater runs lazily (at render), and a DELETE that fails fast can
    // reject before it ever ran.
    const conversationsRef = useRef(conversations);
    conversationsRef.current = conversations;
    const withoutTombstones = (list) => (
        tombstonesRef.current.size
            ? (list || []).filter((c) => !tombstonesRef.current.has(c.id))
            : (list || [])
    );

    const loadConversations = useCallback(async (page = 1, limit = 20) => {
        try {
            setLoading(true);
            setError(null);
            const ticket = ++listReqRef.current;
            const response = await apiService.getConversations(page, limit);
            if (ticket !== listReqRef.current) return;   // superseded (#433)
            setConversations(withoutTombstones(response.conversations));
        } catch (err) {
            console.error('Error loading conversations:', err);
            setError(userErrorMessage(err));
            // Note: 401 handling is done via authEvents in apiService
            // Don't clear tokens here - let the auth system handle logout
        } finally {
            setLoading(false);
        }
    }, []);

    // `silent` (#206 re-open): refresh the list WITHOUT toggling the shared
    // `loading` flag (stale-while-revalidate) — the sidebar keeps showing the
    // existing rows instead of blanking into "Loading conversations...". Used by
    // every background/auth-driven refetch; only the very FIRST cold load is visible.
    const loadAllConversations = useCallback(async ({ silent = false } = {}) => {
        try {
            if (!silent) setLoading(true);
            setError(null);
            // Load with a very high limit to get all conversations
            const ticket = ++listReqRef.current;
            const response = await apiService.getConversations(1, 1000);
            const fresh = withoutTombstones(response.conversations);
            // A delete issued while this was in flight bumps the ticket: the
            // response predates it and must not repaint the deleted row (#433).
            if (ticket === listReqRef.current) setConversations(fresh);
            hasLoadedListRef.current = true;
            return fresh;
        } catch (err) {
            console.error('Error loading all conversations:', err);
            setError(userErrorMessage(err));
            throw err;
        } finally {
            if (!silent) setLoading(false);
        }
    }, []);

    const createConversation = useCallback(async (title = null, modelName = 'llama-local', systemPrompt = null) => {
        try {
            setError(null);
            const newConversation = await apiService.createConversation(title, modelName, systemPrompt);
            setConversations(prev => [newConversation, ...prev]);
            return newConversation;
        } catch (err) {
            console.error('Error creating conversation:', err);
            setError(userErrorMessage(err));
            throw err;
        }
    }, []);

    const loadConversation = useCallback(async (conversationId) => {
        // NOTE: this fetches a SINGLE conversation (fired on every navigation
        // between chats). It must NOT touch the shared `loading` flag — that flag
        // drives the SIDEBAR's "Loading conversations..." list state, and blanking
        // the whole sidebar every time a user opens a chat is the "Jitter (Loading
        // Conversations)" bug (#206). The Main Chat area has its own targeted
        // loading state (loadingConvId → ChatSkeleton in MainOfficeView), so the
        // sidebar list must stay decoupled from a single conversation's fetch
        // lifecycle. `loading` is reserved for the conversation LIST fetch
        // (loadConversations / loadAllConversations).
        try {
            setError(null);
            const conversation = await apiService.getConversation(conversationId);
            setCurrentConversation(conversation);
            return conversation;
        } catch (err) {
            console.error('Error loading conversation:', err);
            setError(userErrorMessage(err));
            throw err;
        }
    }, []);

    const updateConversation = useCallback(async (conversationId, updates) => {
        try {
            setError(null);
            const updatedConversation = await apiService.updateConversation(conversationId, updates);
            
            // Update in conversations list
            setConversations(prev => 
                prev.map(conv => 
                    conv.id === conversationId 
                        ? { ...conv, ...updatedConversation }
                        : conv
                )
            );
            
            // Update current conversation if it's the same
            if (currentConversation?.id === conversationId) {
                setCurrentConversation(prev => ({ ...prev, ...updatedConversation }));
            }
            
            return updatedConversation;
        } catch (err) {
            console.error('Error updating conversation:', err);
            setError(userErrorMessage(err));
            throw err;
        }
    }, [currentConversation]);

    // Inconsistent deletion (#433): OPTIMISTIC. The row leaves the list the
    // instant the user confirms; the DELETE runs behind it. Prod logged the route
    // at 1-3 s (up to 7.6 s) and the row used to stay on screen for all of it, so
    // users clicked Delete again (404s in the logs) or concluded it had failed.
    // On a genuine failure the row is put back where it was and the error is
    // rethrown so the caller can say so. A 404 means it is already gone: success.
    const removeLocally = useCallback((ids) => {
        const idSet = new Set(ids);
        ids.forEach((id) => tombstonesRef.current.add(id));
        listReqRef.current += 1;                    // drop any in-flight list fetch
        const removed = conversationsRef.current
            .map((conv, index) => ({ conv, index }))
            .filter(({ conv }) => idSet.has(conv.id));
        setConversations((prev) => (
            prev.some((conv) => idSet.has(conv.id)) ? prev.filter((conv) => !idSet.has(conv.id)) : prev
        ));
        setCurrentConversation((cur) => (cur && idSet.has(cur.id) ? null : cur));
        return () => removed;
    }, []);

    const restoreLocally = useCallback((ids, getRemoved) => {
        ids.forEach((id) => tombstonesRef.current.delete(id));
        const removed = getRemoved();
        if (!removed.length) return;
        setConversations((prev) => {
            const next = [...prev];
            for (const { conv, index } of removed) {
                if (next.some((c) => c.id === conv.id)) continue;
                next.splice(Math.min(index, next.length), 0, conv);
            }
            return next;
        });
    }, []);


    const deleteConversation = useCallback(async (conversationId) => {
        setError(null);
        const getRemoved = removeLocally([conversationId]);
        try {
            await apiService.deleteConversation(conversationId);
        } catch (err) {
            if (isAlreadyGone(err)) return;
            console.error('Error deleting conversation:', err);
            restoreLocally([conversationId], getRemoved);
            setError(userErrorMessage(err));
            throw err;
        }
    }, [removeLocally, restoreLocally]);

    const bulkDeleteConversations = useCallback(async (conversationIds) => {
        const ids = [...(conversationIds || [])];
        setError(null);
        const getRemoved = removeLocally(ids);
        try {
            await apiService.bulkDeleteConversations(ids);
        } catch (err) {
            console.error('Error bulk deleting conversations:', err);
            restoreLocally(ids, getRemoved);
            setError(userErrorMessage(err));
            throw err;
        }
    }, [removeLocally, restoreLocally]);

    // #448 re-open: the auto-title is generated in the BACKGROUND after the
    // first message's POST returns (it used to block that POST ~3 s, which is
    // the window a reload orphaned the turn in). The server announces it on the
    // live feed; patch the sidebar row and the open conversation in place.
    const applyTitle = useCallback((conversationId, title) => {
        if (!conversationId || !title) return;
        setConversations(prev => prev.map(conv => (
            conv.id === conversationId && conv.title !== title ? { ...conv, title } : conv
        )));
        setCurrentConversation(prev => (
            prev && prev.id === conversationId && prev.title !== title ? { ...prev, title } : prev
        ));
    }, []);
    useEffect(() => {
        const onTitle = (e) => {
            const { conversation_id: cid, title } = e?.detail || {};
            applyTitle(cid, title);
        };
        window.addEventListener('omnis:conversation-title-updated', onTitle);
        return () => window.removeEventListener('omnis:conversation-title-updated', onTitle);
    }, [applyTitle]);
    const titleFallbackTimersRef = useRef({});
    useEffect(() => () => {
        Object.values(titleFallbackTimersRef.current).forEach(clearTimeout);
    }, []);

    const addMessageToConversation = useCallback(async (conversationId, content, messageType = 'user', metadata = null) => {
        try {
            setError(null);
            const message = await apiService.addMessageToConversation(conversationId, content, messageType, metadata);

            // After adding the message, fetch the updated conversation to get the new title.
            //
            // #290 (New conversations transcription bug): the auto-title refresh must
            // key on the TARGET conversation, not the one on screen. A first user
            // message dispatched into a BACKGROUND conversation (an origin-bound
            // voice send flushed after the user navigated away) used to skip this
            // branch because the ON-SCREEN conversation already had a title — so the
            // new conversation's sidebar row kept a null title, which the sidebar's
            // "hide untitled empties" filter then hid until the next full list
            // refetch (the PO's "reappears after 5-10 minutes").
            const targetConversation = currentConversation?.id === conversationId
                ? currentConversation
                : conversations.find(conv => conv.id === conversationId);
            const titleMissing = !targetConversation?.title || targetConversation.title === 'New Conversation';
            if (messageType === 'user' && titleMissing) {
                const updatedConversation = await apiService.getConversation(conversationId);
                
                // Update current conversation with new title and message
                if (currentConversation?.id === conversationId) {
                    setCurrentConversation(prev => ({
                        ...prev,
                        ...updatedConversation,
                        messages: [...(prev.messages || []), message]
                    }));
                }
                
                // Update conversation list with new title
                setConversations(prev => 
                    prev.map(conv => 
                        conv.id === conversationId 
                            ? { ...conv, title: updatedConversation.title || conv.title, updated_at: new Date().toISOString() }
                            : conv
                    ).sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at))
                );
                // #448 re-open: the title is now written by a background thread
                // a few seconds later and announced on the live feed. If this
                // tab misses that event (hidden tab: the feed only polls while
                // visible), one late refetch picks the title up anyway.
                if (!updatedConversation?.title && !titleFallbackTimersRef.current[conversationId]) {
                    titleFallbackTimersRef.current[conversationId] = setTimeout(async () => {
                        delete titleFallbackTimersRef.current[conversationId];
                        try {
                            const later = await apiService.getConversation(conversationId);
                            if (later?.title) applyTitle(conversationId, later.title);
                        } catch (_) { /* the live event / next list load covers it */ }
                    }, 6000);
                }
            } else {
                // Update current conversation if it matches
                if (currentConversation?.id === conversationId) {
                    setCurrentConversation(prev => ({
                        ...prev,
                        messages: [...(prev.messages || []), message],
                        updated_at: new Date().toISOString()
                    }));
                }
                
                // Update conversation list to reflect latest activity
                setConversations(prev => 
                    prev.map(conv => 
                        conv.id === conversationId 
                            ? { ...conv, updated_at: new Date().toISOString() }
                            : conv
                    ).sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at))
                );
            }
            
            return message;
        } catch (err) {
            console.error('Error adding message to conversation:', err);
            setError(userErrorMessage(err));
            throw err;
        }
    }, [currentConversation, conversations, applyTitle]);

    const selectConversation = useCallback((conversation) => {
        setCurrentConversation(conversation);
    }, []);

    const clearCurrentConversation = useCallback(() => {
        setCurrentConversation(null);
    }, []);

    // Load conversations when user is authenticated AND auth is stable
    // This prevents 401 errors from early requests triggering logout.
    // Use loadAllConversations (large single page) rather than the 20-row
    // first page, otherwise older conversations silently fall off the sidebar
    // list and are only reachable via search (Conversations out of sight bug).
    //
    // #206 (re-open) — the "ghost fetch". This effect is keyed on `token`, so it
    // re-runs on EVERY Firebase token rotation, not just the first sign-in. useAuth
    // calls setToken(fresh) from the 50-min refresh timer, the `visibilitychange`
    // tab-focus refresh, the `online` reconnect handler, AND 401 recovery — none of
    // which are user navigation. Each rotation used to call loadAllConversations()
    // WITH the loading flag, spontaneously blanking the sidebar into "Loading
    // conversations..." while the user was typing / recording / idle. Fix: only the
    // FIRST cold load for a session is visible; every subsequent token-driven
    // refetch is SILENT (stale-while-revalidate), so the list never unmounts.
    useEffect(() => {
        if (token && authStable) {
            loadAllConversations({ silent: hasLoadedListRef.current });
        } else if (!token) {
            hasLoadedListRef.current = false;
            setConversations([]);
            setLoading(false);
        }
    }, [loadAllConversations, token, authStable]);

    return {
        conversations,
        currentConversation,
        loading,
        error,
        loadConversations,
        loadAllConversations,
        createConversation,
        loadConversation,
        updateConversation,
        deleteConversation,
        bulkDeleteConversations,
        addMessageToConversation,
        selectConversation,
        clearCurrentConversation
    };
};